import React from 'react';

interface Attachment {
  name: string;
  size: number;
  url: string;
}

interface ViewNoteSectionProps {
  note: {
    coachName: string;
    createdAt: string;
    sessionOverview?: string;
    topicsDiscussed?: string[];
    externalLinks?: string[];
    attachments?: Attachment[];
  };
}

const ViewNoteSection: React.FC<ViewNoteSectionProps> = ({ note }) => {
  return (
    <div className="bg-gray-50 p-[10px] rounded-xl border border-gray-200">


      {/* Coach Name */}
      <div className="textBodySerif mb-4">
        {note.coachName}
      </div>

      {/* Topics Discussed */}
      {note.topicsDiscussed && note.topicsDiscussed.length > 0 && (
        <div className="mb-4">
          <div className="text-noteTitle desktext16 mb-1">Topics Discussed</div>
          <div className="text-noteDesc text-base font-normal">
            {note.topicsDiscussed.join(', ')}
          </div>
        </div>
      )}

      {/* External Links */}
      {note.externalLinks && note.externalLinks.length > 0 && (
        <div className="mb-4">
          <div className="text-noteTitle desktext16 mb-1">External links</div>
          <div className="flex flex-wrap gap-2">
            {note.externalLinks.map((link, idx) => (
              <a
                key={idx}
                href={link.startsWith('http') ? link : `https://${link}`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-blue-50 text-blue-700 text-sm px-3 py-1 rounded-full border border-blue-100 hover:bg-blue-100 transition"
              >
                {link}
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Attachments */}
      {note.attachments && note.attachments.length > 0 && (
        <div className="mb-4">
          <div className="text-noteTitle desktext16 mb-1">Attachment(s)</div>
          <div className="flex flex-col gap-3">
            {note.attachments.map((file, idx) => (
              <a
                key={idx}
                href={file.url}
                download={file.name}
                className="flex items-center gap-3 text-gray-700 hover:text-blue-700 transition"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M9.5 12L12 14.5L14.5 12M12 4.5V13.8912" stroke="#475467" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M20 16.5L19.7785 17.8288C19.6178 18.7932 18.7834 19.5 17.8057 19.5H6.19425C5.21658 19.5 4.3822 18.7932 4.22147 17.8288L4 16.5" stroke="#475467" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                <div>
                  <div className="text-sm font-medium">{file.name}</div>
                  <div className="text-xs text-gray-400">{(file.size / 1024).toFixed(1)} KB</div>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Session Overview */}
      {note.sessionOverview && (
        <div className="mt-4">
          <div className="text-noteTitle desktext16 mb-1">Session overview</div>
          <div
            className="text-noteDesc text-base font-normal overflow-hidden"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 7,
              WebkitBoxOrient: 'vertical',
              whiteSpace: 'pre-line',
              textOverflow: 'ellipsis',
            }}
          >
            {note.sessionOverview}
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewNoteSection;